import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Button from "../common/Button";
import LogoutConfirmationModal from "../common/LogoutConfirmationModal";
import { useStore } from "../../context/StoreContext";
import { useTheme } from "../../context/ThemeContext";
import { useGetOpenTillSessionsQuery } from "../../store/api";
import { useDeviceCashSessionResolution } from "../../hooks/useDeviceCashSession";
import { useStoreFeatures } from "../../hooks/useStoreFeatures";
import { useCurrentOperator } from "../../hooks/useCurrentOperator";

const POSNavbar = ({ onStartSession }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDarkMode, toggleDarkMode } = useTheme();
  const { features } = useStoreFeatures(true);
  const { selectedStoreId } = useStore();
  const { operatorName, operatorInitials } = useCurrentOperator();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  const userMenuRef = useRef(null);

  const posDineInMode = features?.posDineInMode
    ? String(features.posDineInMode).toUpperCase()
    : "";
  const posEnableTakeaway = features?.posEnableTakeaway !== false;
  const showAdmin = true;
  const showDineIn =
    location.pathname.startsWith("/pos/dinein") ||
    location.pathname.startsWith("/pos/table") ||
    location.pathname.startsWith("/pos/cash-up") ||
    location.pathname.startsWith("/pos/cash-movements") ||
    location.pathname.startsWith("/pos/change-payment-method") ||
    location.pathname.startsWith("/pos/reservations");
  const showTakeaway = posEnableTakeaway;
  const adminPath = posDineInMode === "TABLE_DINE_IN" ? "/floorplan/floors" : "/menu/items";
  const dineInPath = posDineInMode === "TABLE_DINE_IN" ? "/pos/table" : "/pos/dinein";
  const isDineInActive =
    location.pathname.startsWith("/pos/dinein") ||
    location.pathname.startsWith("/pos/table");
  const isReservationsActive = location.pathname.startsWith("/pos/reservations");
  const isCashUpActive =
    location.pathname.startsWith("/pos/cash-up") ||
    location.pathname.startsWith("/pos/cash-movements") ||
    location.pathname.startsWith("/pos/change-payment-method");

  const title = (() => {
    if (location.pathname.startsWith("/pos/cash-movements/history/"))
      return "Shift Summary";
    if (location.pathname.startsWith("/pos/cash-movements/history"))
      return "Shift History";
    if (
      location.pathname.startsWith("/pos/cash-up") ||
      location.pathname.startsWith("/pos/cash-movements")
    )
      return "Cash Up";
    if (location.pathname.startsWith("/pos/held-orders")) return "Held Orders";
    if (location.pathname.startsWith("/pos/takeaway")) return "Takeaway";
    if (location.pathname.startsWith("/pos/dinein")) return "Dine-In";
    if (location.pathname.startsWith("/pos/table")) return "Dining Room Control";
    if (posDineInMode === "TABLE_DINE_IN") return "Dining Room Control";
    if (posDineInMode === "DINE_IN") return "Dine-In";
    return "POS";
  })();

  const {
    data: openSessionsData,
    isLoading: openSessionsLoading,
  } = useGetOpenTillSessionsQuery(selectedStoreId, { skip: !selectedStoreId });
  const openSessions = React.useMemo(
    () => (Array.isArray(openSessionsData) ? openSessionsData : []),
    [openSessionsData],
  );
  const deviceSessionResolution = useDeviceCashSessionResolution(
    openSessions,
    Boolean(selectedStoreId),
  );
  const showStartSessionButton =
    Boolean(selectedStoreId) &&
    !openSessionsLoading &&
    !deviceSessionResolution.isLoading &&
    deviceSessionResolution.status === "no_session";

  const currentDate = new Date().toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });

  const handleLogout = () => {
    setUserMenuOpen(false);
    setLogoutConfirmOpen(true);
  };

  useEffect(() => {
    const handleDocumentClick = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleDocumentClick);
    return () => document.removeEventListener("mousedown", handleDocumentClick);
  }, []);

  useEffect(() => {
    const activeNavKey = isDineInActive
      ? "pos-mode"
      : isReservationsActive
        ? "reservations"
        : isCashUpActive
          ? "cash-up"
          : "";
    if (!activeNavKey) return;
    document
      .querySelector(`[data-pos-nav-active="${activeNavKey}"]`)
      ?.focus({ preventScroll: true });
  }, [isCashUpActive, isDineInActive, isReservationsActive, location.pathname]);

  return (
    <div className="pos-navbar sticky top-0 z-50 transition-colors duration-200">
      <div className="flex items-center justify-between px-6 py-3">
        {/* Left: Title with inline time */}
        <div className="flex items-center gap-4">
          <h1 className="text-xl font-black bg-gradient-to-r from-slate-900 to-slate-700 dark:from-slate-100 dark:to-slate-300 bg-clip-text text-transparent">
            {title}
          </h1>
          <span className="hidden sm:inline-block text-sm font-semibold text-slate-500 dark:text-slate-400">
            {currentDate}
          </span>
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-2">
          {/* Dark Mode Toggle */}
          <button
            type="button"
            onClick={toggleDarkMode}
            className="rounded-lg p-2 text-slate-600 dark:text-slate-400 transition-all hover:bg-slate-200 dark:hover:bg-slate-700"
            aria-label="Toggle dark mode"
          >
            {isDarkMode ? (
              <svg
                className="h-5 w-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
                />
              </svg>
            ) : (
              <svg
                className="h-5 w-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
                />
              </svg>
            )}
          </button>

          {/* Admin Panel Button - Subtle */}
          {showAdmin && (
            <Button
              size="sm"
              variant="secondary"
              className="inline-flex items-center whitespace-nowrap"
              onClick={() => {
                localStorage.removeItem("posAdmin.sidebar.scrollTop");
                navigate(adminPath);
              }}
            >
              <svg
                className="h-4 w-4 mr-1.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
              Admin
            </Button>
          )}

          {showStartSessionButton && (
            <Button
              size="sm"
              variant="secondary"
              className="inline-flex items-center whitespace-nowrap"
              onClick={onStartSession}
            >
              Start Session
            </Button>
          )}

          {showTakeaway && (
            <Button
              size="sm"
              variant="secondary"
              className="inline-flex items-center whitespace-nowrap"
              onClick={() => navigate("/pos/takeaway")}
            >
              Takeaway
            </Button>
          )}

          {showDineIn && (
            <Button
              size="sm"
              variant={isDineInActive ? "pos-primary" : "secondary"}
              className="inline-flex items-center whitespace-nowrap"
              data-pos-nav-active={isDineInActive ? "pos-mode" : undefined}
              onClick={() => navigate(dineInPath)}
            >
              POS Mode
            </Button>
          )}

          <Button
            size="sm"
            variant={isCashUpActive ? "pos-primary" : "secondary"}
            className="inline-flex items-center whitespace-nowrap"
            data-pos-nav-active={isCashUpActive ? "cash-up" : undefined}
            onClick={() => navigate("/pos/cash-movements")}
          >
            Cash Up
          </Button>

          {/*
          {showHeldOrders && (
            <Button
              size="sm"
              variant="secondary"
              className="inline-flex items-center whitespace-nowrap"
              onClick={() => navigate("/pos/held-orders")}
            >
              Held Orders{heldCount ? ` (${heldCount})` : ""}
            </Button>
          )}
          */}

          <div className="relative" ref={userMenuRef}>
            <button
              type="button"
              onClick={() => setUserMenuOpen((open) => !open)}
              className="inline-flex max-w-[220px] items-center gap-2 rounded-full border border-slate-200 bg-white/80 px-2.5 py-1 shadow-sm backdrop-blur transition hover:border-slate-300 hover:bg-white dark:border-slate-700 dark:bg-slate-800/80 dark:hover:border-slate-600 dark:hover:bg-slate-800"
              title={`Logged in as ${operatorName}`}
              aria-haspopup="menu"
              aria-expanded={userMenuOpen}
            >
              <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-indigo-100 text-[10px] font-black text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-200">
                {operatorInitials}
              </span>
              <span className="truncate text-[11px] font-semibold text-slate-700 dark:text-slate-200">
                {operatorName}
              </span>
              <svg
                className={`h-3.5 w-3.5 shrink-0 text-slate-500 transition-transform duration-150 ${
                  userMenuOpen ? "rotate-180" : ""
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>

            {userMenuOpen && (
              <div className="absolute right-0 top-full z-50 mt-2 w-44 rounded-2xl border border-slate-200 bg-white p-2 shadow-xl dark:border-slate-700 dark:bg-slate-900">
                <button
                  type="button"
                  onClick={handleLogout}
                  className="flex w-full items-center justify-between rounded-xl px-3 py-2 text-sm font-semibold text-rose-600 transition hover:bg-rose-50 dark:text-rose-300 dark:hover:bg-rose-950/30"
                >
                  <span>Logout</span>
                  <svg
                    className="h-4 w-4 shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                    />
                  </svg>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      <LogoutConfirmationModal
        isOpen={logoutConfirmOpen}
        onClose={() => setLogoutConfirmOpen(false)}
      />
    </div>
  );
};

export default POSNavbar;
